/* ----------------------------------------------------------------------------
A persistent <include-fragment>: loads `src`, then intercepts the links and
forms inside it and swaps its contents with the response. Requests ask for
`text/html; fragment` so the server skips the layout, and a response of any
other type is refused rather than swapped in. The frame is the browsing
context: a link or form (or submitter `formtarget`) that targets `_self`, or
nothing, loads into the frame, while any other target navigates normally. A
frame with `target="_top"` flips that default for its links, since links in a
frame's content usually belong to the page; forms inside it still target the
frame. Confirmations and in-flight button state come from mini-rails-ujs
(data-confirm / data-disable-with).

Like <img>, setting `src` loads it, and `loading="lazy"` waits until the frame
scrolls into view. Links and GET forms navigate the frame by assigning `src`,
so assigning it yourself does the same, which is how a live preview points the
frame at a form's current values; `load()` reloads it. The frame is `aria-busy`
while a request is in flight and dispatches `load` once the swap is done, or
`error` (an ErrorEvent) when the request failed.
---------------------------------------------------------------------------- */

import { csrfToken } from "../../helpers"

const FRAGMENT = "text/html; fragment"

class FragmentFrameElement extends HTMLElement {
  static observedAttributes = ["src"]

  #abortController

  #observer = new IntersectionObserver((entries) => {
    if (entries.some((entry) => entry.isIntersecting)) this.load()
  })

  constructor() {
    super()
    this.addEventListener("click", this.#followLink)
    this.addEventListener("submit", this.#submitForm)
  }

  attributeChangedCallback() {
    if (this.loading === "lazy") this.#observer.observe(this)
    else this.load()
  }

  get src() {
    const src = this.getAttribute("src")
    return src ? new URL(src, document.baseURI).href : ""
  }

  set src(value) {
    this.setAttribute("src", value)
  }

  get target() {
    return this.getAttribute("target") || ""
  }

  set target(value) {
    this.setAttribute("target", value)
  }

  get loading() {
    return this.getAttribute("loading") === "lazy" ? "lazy" : "eager"
  }

  set loading(value) {
    this.setAttribute("loading", value)
  }

  load() {
    if (this.src) return this.#load(this.src)
  }

  #followLink = (event) => {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return

    const link = event.target.closest("a[href]")
    if (!link || link.hasAttribute("download") || link.origin !== location.origin) return

    const target = link.target || this.target
    if (target && target !== "_self") return

    event.preventDefault()
    this.src = link.href
  }

  #submitForm = (event) => {
    if (event.defaultPrevented) return
    const { target: form, submitter } = event

    const method = (submitter?.getAttribute("formmethod") || form.method).toUpperCase()
    if (method === "DIALOG") return

    const target = submitter?.formTarget || form.target
    if (target && target !== "_self") return

    event.preventDefault()
    const url = new URL(submitter?.getAttribute("formaction") || form.action, document.baseURI)
    const body = new FormData(form, submitter)
    if (method === "GET") {
      url.search = new URLSearchParams(body)
      this.src = url
    } else {
      this.#load(url, { method, body })
    }
  }

  async #load(url, init) {
    this.#observer.unobserve(this)
    this.#abortController?.abort()
    this.#abortController = new AbortController()
    const { signal } = this.#abortController
    this.setAttribute("aria-busy", "true")
    try {
      this.#render(await fetchFragment(url, { ...init, signal }))
      this.dispatchEvent(new Event("load"))
    } catch (error) {
      if (signal.aborted) return
      this.dispatchEvent(new ErrorEvent("error", { error }))
      throw error
    } finally {
      if (!signal.aborted) this.removeAttribute("aria-busy")
    }
  }

  #render(html) {
    // Parsed in a detached <template> rather than assigned to innerHTML, which
    // drops a fragment's own <form> tags whenever the frame sits inside a form.
    const template = document.createElement("template")
    template.innerHTML = html
    this.replaceChildren(template.content)
    this.querySelector("[autofocus]")?.focus()
  }
}

async function fetchFragment(url, init) {
  const headers = { Accept: FRAGMENT, "X-CSRF-Token": csrfToken() }
  const response = await fetch(url, { ...init, headers })
  if (!response.headers.get("Content-Type")?.includes(FRAGMENT)) {
    throw new Error(`Expected a "${FRAGMENT}" response from ${response.url}`)
  }
  return response.text()
}

customElements.define("fragment-frame", FragmentFrameElement)
